import { useMemo, useState } from "react";
import type { ReactNode } from "react";

import { VideoCard } from "../components/VideoCard";
import { useCatalog } from "../data/hooks";
import type { CatalogEntry, ThemeScores } from "../lib/types";

interface Option {
  readonly value: string;
  readonly label: string;
}

interface Filters {
  q: string;
  channel: string;
  series: string;
  speaker: string;
  language: string;
  year: string;
  duration: string;
  theme: string;
  harmony: number;
  wellbeing: number;
  peace: number;
  sort: string;
}

const INITIAL: Filters = {
  q: "",
  channel: "",
  series: "",
  speaker: "",
  language: "",
  year: "",
  duration: "",
  theme: "",
  harmony: 0,
  wellbeing: 0,
  peace: 0,
  sort: "relevance",
};

const PAGE = 60;

const SCORE_STEPS = [0, 1, 2, 3].map((step) => (
  <option key={step} value={String(step)}>
    {step === 0 ? "Any" : `${step} or more`}
  </option>
));

function scoreSum(entry: CatalogEntry): number {
  if (entry.scores === null) {
    return 0;
  }
  return entry.scores.harmony + entry.scores.wellbeing + entry.scores.peace;
}

function themeScore(scores: ThemeScores, theme: string): number {
  if (theme === "harmony") {
    return scores.harmony;
  }
  if (theme === "wellbeing") {
    return scores.wellbeing;
  }
  return scores.peace;
}

function withinDuration(seconds: number, band: string): boolean {
  if (band === "short") {
    return seconds < 300;
  }
  if (band === "medium") {
    return seconds >= 300 && seconds <= 1200;
  }
  if (band === "long") {
    return seconds > 1200;
  }
  return true;
}

function meetsScore(value: number | undefined, min: number): boolean {
  return min === 0 || (value !== undefined && value >= min);
}

function optionsBy(
  entries: readonly CatalogEntry[],
  pick: (entry: CatalogEntry) => string | null,
): Option[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const key = pick(entry);
    if (key !== null && key.length > 0) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([value, count]) => ({ value, label: `${value} (${count})` }));
}

function Field({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-warm-gray">
      <span className="uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-hairline bg-warm-white px-2 py-1.5 text-sm text-warm-brown"
      >
        {children}
      </select>
    </label>
  );
}

export function Library() {
  const { entries, error } = useCatalog();
  const [filters, setFilters] = useState<Filters>(INITIAL);
  const [limit, setLimit] = useState(PAGE);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const facets = useMemo(() => {
    const all = entries ?? [];
    const speakerNames = new Map<string, string>();
    for (const entry of all) {
      speakerNames.set(entry.speakerKey, entry.speaker);
    }
    return {
      series: optionsBy(all, (entry) => entry.series),
      speakers: [...speakerNames.entries()]
        .map(([key, name]) => ({
          value: key,
          label: name,
          count: all.filter((entry) => entry.speakerKey === key).length,
        }))
        .sort((a, b) => b.count - a.count),
      languages: optionsBy(all, (entry) => entry.language),
      channels: optionsBy(all, (entry) => entry.channel),
      years: [...new Set(all.map((entry) => entry.year))]
        .filter((year): year is number => year !== null)
        .sort((a, b) => b - a),
    };
  }, [entries]);

  const results = useMemo(() => {
    const all = entries ?? [];
    const q = filters.q.trim().toLowerCase();
    const filtered = all.filter((entry) => {
      if (q.length > 0 && !entry.title.toLowerCase().includes(q)) {
        return false;
      }
      if (filters.channel.length > 0 && entry.channel !== filters.channel) {
        return false;
      }
      if (filters.series.length > 0 && entry.series !== filters.series) {
        return false;
      }
      if (filters.speaker.length > 0 && entry.speakerKey !== filters.speaker) {
        return false;
      }
      if (filters.language.length > 0 && entry.language !== filters.language) {
        return false;
      }
      if (filters.year.length > 0 && String(entry.year) !== filters.year) {
        return false;
      }
      if (!withinDuration(entry.duration, filters.duration)) {
        return false;
      }
      if (
        filters.theme.length > 0 &&
        (entry.scores === null || themeScore(entry.scores, filters.theme) < 2)
      ) {
        return false;
      }
      if (
        !meetsScore(entry.scores?.harmony, filters.harmony) ||
        !meetsScore(entry.scores?.wellbeing, filters.wellbeing) ||
        !meetsScore(entry.scores?.peace, filters.peace)
      ) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (filters.sort) {
        case "newest":
          return (b.year ?? 0) - (a.year ?? 0);
        case "oldest":
          return (a.year ?? 9999) - (b.year ?? 9999);
        case "longest":
          return b.duration - a.duration;
        case "shortest":
          return a.duration - b.duration;
        case "title":
          return a.title.localeCompare(b.title);
        default:
          return scoreSum(b) - scoreSum(a);
      }
    });
    return sorted;
  }, [entries, filters]);

  if (error) {
    return (
      <p className="text-warm-gray">
        The library data could not be loaded. Run{" "}
        <code className="font-mono text-sm">pnpm build:index</code> first.
      </p>
    );
  }
  if (entries === null) {
    return <p className="text-warm-gray">Loading the library…</p>;
  }

  const set = (patch: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setLimit(PAGE);
  };
  const shown = results.slice(0, limit);
  const advancedActive =
    filters.channel.length > 0 ||
    filters.series.length > 0 ||
    filters.language.length > 0 ||
    filters.year.length > 0 ||
    filters.duration.length > 0 ||
    filters.harmony > 0 ||
    filters.wellbeing > 0 ||
    filters.peace > 0;
  const anyActive =
    advancedActive ||
    filters.q.length > 0 ||
    filters.speaker.length > 0 ||
    filters.theme.length > 0;

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">All talks</p>
        <h1 className="font-display text-3xl text-warm-brown">
          Browse {entries.length} discourses
        </h1>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-warm-gray">
          <span className="uppercase tracking-wide">Find by title</span>
          <input
            value={filters.q}
            onChange={(event) => set({ q: event.target.value })}
            placeholder="Title contains…"
            className="rounded-md border border-hairline bg-warm-white px-2 py-1.5 text-sm text-warm-brown"
          />
        </label>
        <Field
          label="Speaker"
          value={filters.speaker}
          onChange={(v) => set({ speaker: v })}
        >
          <option value="">All speakers</option>
          {facets.speakers.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label} ({option.count})
            </option>
          ))}
        </Field>
        <Field
          label="Theme"
          value={filters.theme}
          onChange={(v) => set({ theme: v })}
        >
          <option value="">Any theme</option>
          <option value="harmony">Harmony of faiths</option>
          <option value="wellbeing">Inner wellbeing</option>
          <option value="peace">Universal peace</option>
        </Field>
        <Field
          label="Sort"
          value={filters.sort}
          onChange={(v) => set({ sort: v })}
        >
          <option value="relevance">Suggested</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="longest">Longest</option>
          <option value="shortest">Shortest</option>
          <option value="title">Title A–Z</option>
        </Field>
        <button
          type="button"
          onClick={() => setShowAdvanced((value) => !value)}
          className="rounded-md border border-hairline px-3 py-2 text-xs text-warm-brown hover:border-gold/50"
          aria-expanded={showAdvanced}
        >
          {showAdvanced ? "Fewer filters" : "More filters"}
          {advancedActive && !showAdvanced ? (
            <span className="ml-1 text-terra">•</span>
          ) : null}
        </button>
        {anyActive ? (
          <button
            type="button"
            onClick={() => set(INITIAL)}
            className="text-xs text-warm-gray hover:text-terra"
          >
            Reset
          </button>
        ) : null}
      </div>

      {showAdvanced ? (
        <div className="mb-6 flex flex-wrap items-end gap-3 border-b border-hairline pb-6">
          <Field
            label="Series"
            value={filters.series}
            onChange={(v) => set({ series: v })}
          >
            <option value="">All series</option>
            {facets.series.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
          <Field
            label="Language"
            value={filters.language}
            onChange={(v) => set({ language: v })}
          >
            <option value="">Any language</option>
            {facets.languages.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
          <Field
            label="Year"
            value={filters.year}
            onChange={(v) => set({ year: v })}
          >
            <option value="">Any year</option>
            {facets.years.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </Field>
          <Field
            label="Length"
            value={filters.duration}
            onChange={(v) => set({ duration: v })}
          >
            <option value="">Any length</option>
            <option value="short">Under 5 min</option>
            <option value="medium">5–20 min</option>
            <option value="long">Over 20 min</option>
          </Field>
          <Field
            label="Channel"
            value={filters.channel}
            onChange={(v) => set({ channel: v })}
          >
            <option value="">Both channels</option>
            {facets.channels.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Field>
          <Field
            label="Harmony score"
            value={String(filters.harmony)}
            onChange={(v) => set({ harmony: Number(v) })}
          >
            {SCORE_STEPS}
          </Field>
          <Field
            label="Wellbeing score"
            value={String(filters.wellbeing)}
            onChange={(v) => set({ wellbeing: Number(v) })}
          >
            {SCORE_STEPS}
          </Field>
          <Field
            label="Peace score"
            value={String(filters.peace)}
            onChange={(v) => set({ peace: Number(v) })}
          >
            {SCORE_STEPS}
          </Field>
        </div>
      ) : null}

      <p className="mb-4 text-sm text-warm-gray">
        {results.length} {results.length === 1 ? "talk" : "talks"}
      </p>

      {shown.length === 0 ? (
        <p className="text-warm-gray">Nothing matches those filters yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((entry) => (
            <VideoCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      {limit < results.length ? (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setLimit((value) => value + PAGE)}
            className="rounded-full border border-hairline px-5 py-2 text-sm text-warm-brown hover:border-gold/50"
          >
            Show more
          </button>
        </div>
      ) : null}
    </div>
  );
}
